import type {
  PreCheckInByDayResponse,
  PreCheckInByDayRow,
  PreCheckInByDayTotals,
  PreCheckOutByDayResponse,
  PreCheckOutByDayRow,
  PreCheckOutByDayTotals,
} from "@/types/analytics-overview";
import {
  downloadAnalyticsExport,
  type AnalyticsExportFormat,
  type AnalyticsExportTableSection,
} from "../../utils/analyticsFileExport";

const PRE_CHECK_IN_HEADERS = [
  "Day",
  "Start",
  "Contact information",
  "Found booking info",
  "Upgrade room",
  "Select document type",
  "Additional information",
  "Payment for stay",
  "Additional details",
  "Plan out",
  "Completed",
  "Email sent",
  "Email delivered",
  "Email opened",
] as const;

const PRE_CHECK_OUT_HEADERS = [
  "Day",
  "Start",
  "Additional services",
  "Bill summary",
  "Review",
  "Completed",
  "Email sent",
  "Email delivered",
  "Email opened",
] as const;

function preCheckInToRow(
  day: string,
  metrics: PreCheckInByDayRow | PreCheckInByDayTotals,
): Array<string | number> {
  return [
    day,
    metrics.start,
    metrics.contactInformation,
    metrics.foundBookingInfo,
    metrics.upgradeRoom,
    metrics.selectDocumentType,
    metrics.additionalInfo,
    metrics.paymentForStay,
    metrics.additionalDetails,
    metrics.planOut,
    metrics.completed,
    metrics.emailSent,
    metrics.emailDelivered,
    metrics.emailOpened,
  ];
}

function preCheckOutToRow(
  day: string,
  metrics: PreCheckOutByDayRow | PreCheckOutByDayTotals,
): Array<string | number> {
  return [
    day,
    metrics.start,
    metrics.additionalServices,
    metrics.billSummary,
    metrics.review,
    metrics.completed,
    metrics.emailSent,
    metrics.emailDelivered,
    metrics.emailOpened,
  ];
}

function buildPreCheckInSections(
  data: PreCheckInByDayResponse,
): AnalyticsExportTableSection[] {
  return [
    {
      title: "Pre-Check-In — Totals",
      headers: [...PRE_CHECK_IN_HEADERS],
      rows: [preCheckInToRow("Total", data.totals)],
    },
    {
      title: "Pre-Check-In — By day",
      headers: [...PRE_CHECK_IN_HEADERS],
      rows: data.series.map((row) => preCheckInToRow(row.day, row)),
    },
  ];
}

function buildPreCheckOutSections(
  data: PreCheckOutByDayResponse,
): AnalyticsExportTableSection[] {
  return [
    {
      title: "Pre-Check-Out — Totals",
      headers: [...PRE_CHECK_OUT_HEADERS],
      rows: [preCheckOutToRow("Total", data.totals)],
    },
    {
      title: "Pre-Check-Out — By day",
      headers: [...PRE_CHECK_OUT_HEADERS],
      rows: data.series.map((row) => preCheckOutToRow(row.day, row)),
    },
  ];
}

export function downloadGuestPathPreCheckInExport(params: {
  format: AnalyticsExportFormat;
  hotelId: string;
  from: string;
  to: string;
  data: PreCheckInByDayResponse;
}): boolean {
  const sections = buildPreCheckInSections(params.data);
  if (sections.every((section) => section.rows.length === 0)) {
    return false;
  }

  downloadAnalyticsExport(
    params.format,
    sections,
    `pre-check-in-${params.hotelId}-${params.from}-${params.to}`,
  );
  return true;
}

export function downloadGuestPathPreCheckOutExport(params: {
  format: AnalyticsExportFormat;
  hotelId: string;
  from: string;
  to: string;
  data: PreCheckOutByDayResponse;
}): boolean {
  const sections = buildPreCheckOutSections(params.data);
  if (sections.every((section) => section.rows.length === 0)) {
    return false;
  }

  downloadAnalyticsExport(
    params.format,
    sections,
    `pre-check-out-${params.hotelId}-${params.from}-${params.to}`,
  );
  return true;
}
